/**
 * Approve-and-schedule button + dialog (SCH.3).
 *
 * Approving a request is also where the supervisor commits a date: a calendar
 * dialog (showing the existing schedule + the PM's requested window as the
 * default) lets them pick the target day/range. That seeds the task times
 * server-side, so the project gets a real `scheduled` window and the PM is
 * notified of the committed date. "Approve without scheduling" stays available
 * for when the date isn't settled yet.
 *
 * Shared by the supervisor queue (Home) and the project detail screen.
 */
import { useState } from 'react';
import { CalendarClock, CheckCircle2, Loader2, Mail, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useApproveProject,
  useBidEmailPreview,
  useProposeSchedule,
  usePickupSuggestions,
  useRequestChanges,
} from '@/api/projects';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Label, Textarea } from '@/components/ui/Input';
import type { ScheduleDay, ProjectApproveInput, BidMode } from '@vari-crew/schemas';
import { CalendarDatePicker } from '@/components/ui/CalendarDatePicker';
import { DailyHoursPicker, isWeekendDay } from '@/components/ui/DailyHoursPicker';
import { dayToIso, isoToDay, parseDay, scheduleHoursLabel } from '@/lib/calendarGrid';
import { formatMoneyCents } from '@/lib/format';
import { cn } from '@/lib/cn';

export interface ApproveScheduleButtonProps {
  project: {
    id: string;
    title: string;
    requested_start_date: string | null;
    requested_end_date?: string | null;
    requested_schedule?: ScheduleDay[] | null;
    requested_schedule_flexible?: boolean;
    deadline_date?: string | null;
    /** Saved bid total — when set, the send-back can include it for the PM (§A6c). */
    bid_total_cents?: number | null;
    /** Set once the bid is approved — gates whether approving SENDS the bid (demo #5). */
    bid_approved_at?: string | null;
    /** none | internal | approval — only 'approval' sends the bid to the requestor. */
    bid_mode?: BidMode;
  };
  label?: string;
  variant?: 'default' | 'outline';
}

function fmt(day: string | null): string {
  return day
    ? parseDay(day).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : '';
}

export function ApproveScheduleButton({
  project,
  label = 'Approve',
  variant = 'outline',
}: ApproveScheduleButtonProps) {
  const approve = useApproveProject(project.id);
  const propose = useProposeSchedule(project.id);
  const requestChanges = useRequestChanges(project.id);
  const reqStart = isoToDay(project.requested_start_date);
  const reqEnd = isoToDay(project.requested_end_date ?? null);
  // Context for the picker: the window/hours the PM asked for + the hard deadline.
  const requestedDays = (project.requested_schedule ?? []).map((s) => s.date);
  const deadlineDay = isoToDay(project.deadline_date ?? null);
  const requestedHours = scheduleHoursLabel(
    project.requested_schedule,
    project.requested_schedule_flexible,
  );
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState<string | null>(reqStart);
  const [end, setEnd] = useState<string | null>(reqEnd);
  const [schedule, setSchedule] = useState<ScheduleDay[] | null>(
    project.requested_schedule ?? null,
  );
  const [pickupDate, setPickupDate] = useState<string | null>(null);
  const [pickupSlot, setPickupSlot] = useState<'09:00' | '14:00' | 'custom'>('09:00');
  const [pickupCustom, setPickupCustom] = useState('09:00');
  const [sendBack, setSendBack] = useState(false);
  const [note, setNote] = useState('');
  const [shareBudget, setShareBudget] = useState(false);
  // Weekend toggle shared by the calendar + the per-day schedule. Starts on only
  // if the PM's requested schedule already named a weekend day.
  const [includeWeekends, setIncludeWeekends] = useState(() =>
    (project.requested_schedule ?? []).some((s) => isWeekendDay(s.date)),
  );
  // Email-preview iframe grows to its content height (capped to the viewport) so
  // the whole email shows without an inner scrollbar.
  const [previewH, setPreviewH] = useState(420);
  const hasBid = project.bid_total_cents != null;
  // A bid that still needs the requestor's approval — approving then SENDS it for
  // budget approval (demo #5) instead of committing the schedule. Once the bid is
  // approved, approving just schedules normally.
  const needsBidApproval = project.bid_mode === 'approval' && !project.bid_approved_at;
  const busy = approve.isPending || propose.isPending || requestChanges.isPending;
  // The API would silently snap a weekend pickup to the next weekday — the PM
  // would then be told a date the supervisor never saw. Surface it instead.
  const pickupOnWeekend = pickupDate != null && isWeekendDay(pickupDate);

  // Live preview of the bid approval email (demo #5) — shown beside the controls
  // when a bid is involved so the supervisor sees exactly what the requestor gets
  // before sending. Refetches as the draft schedule / pickup change.
  const pickupTimeValue = pickupDate ? (pickupSlot === 'custom' ? pickupCustom : pickupSlot) : null;
  const showEmailPreview = project.bid_mode === 'approval' && !sendBack;
  const previewBody: ProjectApproveInput | null = showEmailPreview
    ? ({
        schedule: schedule ?? null,
        pickup_date: pickupDate || null,
        pickup_time: pickupTimeValue,
      } as ProjectApproveInput)
    : null;
  const emailPreview = useBidEmailPreview(project.id, previewBody, open && showEmailPreview);

  // Suggested pickup days (demo #7, F20): other projects' scheduled pickups that
  // fall before this install, so the crew can consolidate onto a truck already
  // going out. Weekends excluded (pickups run Mon–Fri).
  const suggestionsQuery = usePickupSuggestions(project.id, open && !sendBack);
  const pickupSuggestions = (suggestionsQuery.data?.suggestions ?? []).filter(
    (s) => (!start || s.date < start) && !isWeekendDay(s.date),
  );

  function submit(withSchedule: boolean) {
    const body =
      withSchedule && start
        ? {
            scheduled_start_date: start,
            scheduled_end_date: end ?? null,
            schedule: schedule ?? null,
            pickup_date: pickupDate || null,
            pickup_time: pickupDate
              ? pickupSlot === 'custom'
                ? pickupCustom
                : pickupSlot
              : null,
          }
        : {};
    approve.mutate(body, {
      onSuccess: () => {
        toast.success(
          withSchedule && start
            ? needsBidApproval
              ? `"${project.title}" — bid sent to the requestor for approval`
              : `"${project.title}" scheduled & approved`
            : `"${project.title}" approved`,
        );
        setOpen(false);
      },
      onError: (err) => toast.error(`Approve failed: ${err.message}`),
    });
  }

  function proposeToPm() {
    if (!start || !schedule || schedule.length === 0) return;
    propose.mutate(
      {
        scheduled_start_date: start,
        scheduled_end_date: end ?? null,
        schedule,
        pickup_date: pickupDate || null,
        pickup_time: pickupDate ? (pickupSlot === 'custom' ? pickupCustom : pickupSlot) : null,
      },
      {
        onSuccess: () => {
          toast.success(`Proposed to the PM — awaiting their confirmation`);
          setOpen(false);
        },
        onError: (err) => toast.error(`Propose failed: ${err.message}`),
      },
    );
  }

  function sendToPm() {
    if (!note.trim()) return;
    requestChanges.mutate(
      {
        note: note.trim(),
        proposed_start_date: start ? dayToIso(start) : null,
        share_budget: hasBid ? shareBudget : undefined,
      },
      {
        onSuccess: () => {
          toast.success(`Sent back to the PM for revision`);
          setOpen(false);
        },
        onError: (err) => toast.error(`Send-back failed: ${err.message}`),
      },
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant={variant}
        onClick={() => {
          setStart(reqStart);
          setEnd(reqEnd);
          setSchedule(project.requested_schedule ?? null);
          setIncludeWeekends((project.requested_schedule ?? []).some((s) => isWeekendDay(s.date)));
          setPickupDate(null);
          setPickupSlot('09:00');
          setPickupCustom('09:00');
          setSendBack(false);
          setNote('');
          setShareBudget(false);
          setOpen(true);
        }}
      >
        <CheckCircle2 className="w-3.5 h-3.5" />
        {label}
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        busy={busy}
        className={showEmailPreview ? 'max-w-5xl' : 'max-w-lg'}
        title={
          <>
            <CalendarClock className="w-4 h-4" />
            Schedule &amp; approve
          </>
        }
      >
        <div className="p-4">
          <div className={showEmailPreview ? 'grid grid-cols-1 lg:grid-cols-2 gap-5 items-start' : ''}>
            <div className="space-y-3">
                <div>
                  <div className="font-medium text-sm truncate">{project.title}</div>
                  {reqStart ? (
                    <div className="text-[11px] text-ink-faint">
                      PM requested {fmt(reqStart)}
                      {reqEnd && reqEnd !== reqStart ? ` – ${fmt(reqEnd)}` : ''} — confirm
                      or pick a different window.
                    </div>
                  ) : (
                    <div className="text-[11px] text-ink-faint">
                      No date requested — pick when the crew will go out.
                    </div>
                  )}
                </div>
                <CalendarDatePicker
                  start={start}
                  end={end}
                  onChange={(r) => {
                    setStart(r.start);
                    setEnd(r.end);
                  }}
                  requestedDays={requestedDays}
                  deadline={deadlineDay}
                  hoursLabel={requestedHours}
                  includeWeekends={includeWeekends}
                  onIncludeWeekendsChange={setIncludeWeekends}
                />
                {!sendBack && (
                  <div className="space-y-3 rounded-lg border border-line p-3">
                    <DailyHoursPicker
                      start={start}
                      end={end}
                      value={schedule}
                      onChange={setSchedule}
                      includeWeekends={includeWeekends}
                    />
                    <div>
                      <Label htmlFor="pickup-date">Inventory pickup (warehouse)</Label>
                      <p className="text-[11px] text-ink-faint mb-1">
                        When the crew pulls inventory — weekdays only. The PM is
                        notified so they can coordinate with the warehouse.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          id="pickup-date"
                          type="date"
                          value={pickupDate ?? ''}
                          onChange={(e) => setPickupDate(e.target.value || null)}
                          className="h-9 rounded-md border border-line bg-transparent px-2 text-sm"
                        />
                        <select
                          aria-label="Pickup time"
                          value={pickupSlot}
                          onChange={(e) =>
                            setPickupSlot(e.target.value as typeof pickupSlot)
                          }
                          disabled={!pickupDate}
                          className="h-9 rounded-md border border-line bg-transparent px-2 text-sm disabled:opacity-50"
                        >
                          <option value="09:00">9:00 AM</option>
                          <option value="14:00">2:00 PM</option>
                          <option value="custom">Custom…</option>
                        </select>
                      </div>
                      {pickupOnWeekend && (
                        <p className="text-[11px] text-status-blocked-fg mt-1" role="alert">
                          That's a {fmt(pickupDate)} — pickups run Mon–Fri. Pick a
                          weekday.
                        </p>
                      )}
                      {pickupDate && pickupSlot === 'custom' && (
                        <input
                          aria-label="Custom pickup time"
                          type="time"
                          value={pickupCustom}
                          onChange={(e) => setPickupCustom(e.target.value)}
                          className="mt-2 w-36 h-9 rounded-md border border-line bg-transparent px-2 text-sm"
                        />
                      )}
                      {/* Consolidate onto a day a truck is already going out (F20). */}
                      {pickupSuggestions.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span className="text-[11px] text-ink-faint">Truck already out:</span>
                          {pickupSuggestions.map((s) => (
                            <button
                              key={s.date}
                              type="button"
                              onClick={() => setPickupDate(s.date)}
                              title={s.projects.join(', ')}
                              className={cn(
                                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                                pickupDate === s.date
                                  ? 'border-accent bg-accent/10 text-accent'
                                  : 'border-line text-ink-dim hover:bg-fill-soft',
                              )}
                            >
                              {fmt(s.date)} · {s.pickup_count} pickup{s.pickup_count > 1 ? 's' : ''}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {sendBack && (
                  <div>
                    <Label htmlFor="sendback-note">What should the PM change?</Label>
                    <Textarea
                      id="sendback-note"
                      rows={3}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Crew is booked that week — can we push to the 26th?"
                    />
                    <p className="text-[11px] text-ink-faint mt-1">
                      {start ? `Proposing ${fmt(start)}. ` : ''}The PM revises the dates and
                      resubmits.
                    </p>
                    {hasBid ? (
                      <label className="flex items-center gap-2 text-sm mt-2">
                        <input
                          type="checkbox"
                          checked={shareBudget}
                          onChange={(e) => setShareBudget(e.target.checked)}
                        />
                        Include the budget ({formatMoneyCents(project.bid_total_cents ?? 0)}) so the
                        requestor can see it
                      </label>
                    ) : (
                      <p className="text-[11px] text-ink-faint mt-2">
                        Set a bid in the Bid card to include a budget with this send-back.
                      </p>
                    )}
                  </div>
                )}

                {sendBack ? (
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <button
                      type="button"
                      className="text-[11px] text-ink-faint hover:text-ink underline disabled:opacity-50"
                      disabled={busy}
                      onClick={() => setSendBack(false)}
                    >
                      ← Back to approve
                    </button>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={busy}>
                        Cancel
                      </Button>
                      <Button size="sm" onClick={sendToPm} disabled={busy || !note.trim()}>
                        {requestChanges.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        <Undo2 className="w-3.5 h-3.5" />
                        Send to PM
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <div className="flex flex-col items-start gap-1">
                      {!needsBidApproval && (
                        <button
                          type="button"
                          className="text-[11px] text-ink-faint hover:text-ink underline disabled:opacity-50"
                          disabled={busy}
                          onClick={() => submit(false)}
                        >
                          Approve without scheduling
                        </button>
                      )}
                      <button
                        type="button"
                        className="text-[11px] text-ink-faint hover:text-ink underline disabled:opacity-50"
                        disabled={busy}
                        onClick={() => setSendBack(true)}
                      >
                        Send back for revision…
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={busy}>
                        Cancel
                      </Button>
                      {!needsBidApproval && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={proposeToPm}
                          disabled={busy || !start || !schedule?.length || pickupOnWeekend}
                          title="Send these dates to the PM to confirm before they're locked in"
                        >
                          {propose.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                          Propose to PM
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => submit(true)}
                        disabled={busy || !start || pickupOnWeekend || (needsBidApproval && !schedule?.length)}
                        title={
                          needsBidApproval
                            ? 'Sends the bid (with the schedule + pickup date) to the requestor for approval'
                            : undefined
                        }
                      >
                        {approve.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                        {needsBidApproval ? 'Send for budget approval' : 'Approve & schedule'}
                      </Button>
                    </div>
                  </div>
                )}
            </div>
            {showEmailPreview && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-medium text-ink-faint">
                  <Mail className="w-3.5 h-3.5" />
                  Email preview — what the requestor receives
                </div>
                {emailPreview.isLoading ? (
                  <div
                    className="rounded-md border border-line grid place-items-center text-ink-faint"
                    style={{ height: previewH }}
                  >
                    <Loader2 className="w-5 h-5 animate-spin" />
                  </div>
                ) : emailPreview.data ? (
                  <iframe
                    title="Bid approval email preview"
                    srcDoc={emailPreview.data.html}
                    // allow-same-origin (no allow-scripts) lets us read the rendered
                    // height to size the frame; the email has no scripts to run.
                    sandbox="allow-same-origin"
                    onLoad={(e) => {
                      const doc = e.currentTarget.contentDocument;
                      const h = doc?.documentElement?.scrollHeight;
                      if (h) setPreviewH(Math.min(h + 4, Math.round(window.innerHeight * 0.8)));
                    }}
                    style={{ height: previewH }}
                    className="w-full rounded-md border border-line bg-white"
                  />
                ) : (
                  <div
                    className="rounded-md border border-line grid place-items-center text-xs text-ink-faint"
                    style={{ height: previewH }}
                  >
                    Preview unavailable.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}
